import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Button,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Stack,
    CircularProgress,
    TablePagination
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { apiList } from "../../services/ApiServices";
import { notifications } from '@mantine/notifications';

const FeePaymentStatus = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchName, setSearchName] = useState("");
    const [selectedClass, setSelectedClass] = useState("");

    // Classes for filter - in a real app, fetch from API
    const classesList = ["9", "10", "11", "12"];

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const navigate = useNavigate();

    const fetchDues = async () => {
        setLoading(true);
        try {
            // Pass page and limit to the API
            // Note: Material UI uses 0-based page index, API likely uses 1-based
            const response = await api.get(`${apiList.getFeeStructures}?page=${page + 1}&limit=${rowsPerPage}`);

            if (response.data && response.data.structures) {
                setStudents(response.data.structures);
                if (response.data.pagination) {
                    setTotalCount(response.data.pagination.total_count);
                } else {
                    setTotalCount(response.data.structures.length);
                }
            } else if (Array.isArray(response.data)) {
                // Fallback for legacy array response
                setStudents(response.data);
                setTotalCount(response.data.length);
            } else {
                setStudents([]);
                console.error("Fee Payment API returned unexpected format:", response.data);
            }
        } catch (error) {
            console.error("Error fetching fee payment status:", error);
            notifications.show({
                title: 'Error',
                message: 'Failed to fetch fee payment status',
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDues();
    }, [page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatus = (student) => {
        const total = parseFloat(student.assignment_total_fee || student.total_annual_fee || 0);
        const paid = parseFloat(student.paid_amount || 0);
        const due = total - paid;

        if (due <= 0 && total > 0) return "Paid";
        if (paid > 0 && due > 0) return "Partial";
        if (paid === 0 && total > 0) return "Pending";
        return "Pending";
    };

    const getChipColor = (status) => {
        switch (status) {
            case "Paid": return "success";
            case "Partial": return "warning";
            case "Pending": return "error";
            default: return "info";
        }
    };

    const filteredStudents = students.filter((student) =>
        (student.student_name?.toLowerCase().includes(searchName.toLowerCase()) ||
            student.roll_number?.toLowerCase().includes(searchName.toLowerCase()) ||
            student.structure_name?.toLowerCase().includes(searchName.toLowerCase())) && // Include structure_name fallback
        (selectedClass ? student.class_name === selectedClass : true)
    );

    const totalDueAmount = students.reduce(
        (sum, s) => sum + (parseFloat(s.assignment_total_fee || s.total_annual_fee || 0) - parseFloat(s.paid_amount || 0)),
        0
    );

    const pendingCount = students.filter(
        (s) => (parseFloat(s.assignment_total_fee || s.total_annual_fee || 0) - parseFloat(s.paid_amount || 0)) > 0
    ).length;

    const handlePayNow = (student) => {
        navigate("/fees/collect", { state: { student } });
    };

    if (loading) {
        return (
            <Box p={3} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h5" fontWeight="bold" mb={3}>
                Fee Payment Status
            </Typography>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search Student / Roll No"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Select Class"
                                value={selectedClass}
                                onChange={(e) => setSelectedClass(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {classesList.map((cls) => (
                                    <MenuItem key={cls} value={cls}>Class {cls}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Summary Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary">Total Due Amount</Typography>
                            <Typography variant="h4" color="error">
                                ₹ {totalDueAmount.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary">Students with Dues</Typography>
                            <Typography variant="h4">
                                {pendingCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Table */}
            <TablePagination
                component="div"
                count={parseInt(totalCount, 10)}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ mb: 2 }}
            />
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eef0fb", borderRadius: 3, overflow: 'hidden' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f4f5ff' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Roll No</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Class</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Total Fee</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Paid</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Due</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Status</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#4d44b5', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e2ff', py: 2 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredStudents.length > 0 ? (
                            filteredStudents.map((row) => {
                                const total = parseFloat(row.assignment_total_fee || row.total_annual_fee || 0);
                                const paid = parseFloat(row.paid_amount || 0);
                                const due = total - paid;
                                const status = getStatus(row);

                                return (
                                    <TableRow key={row.structure_id} hover>
                                        <TableCell>{row.roll_number || '-'}</TableCell>
                                        <TableCell>{row.student_name || row.structure_name}</TableCell>
                                        <TableCell>{row.class_name}</TableCell>
                                        <TableCell align="right">₹ {total.toLocaleString()}</TableCell>
                                        <TableCell align="right">₹ {paid.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: due > 0 ? 'error.main' : 'success.main' }}>
                                            ₹ {due.toLocaleString()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={status}
                                                color={getChipColor(status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {due > 0 && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handlePayNow(row)}
                                                >
                                                    Collect Fee
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No students found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default FeePaymentStatus;
